import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getProfessionGuide, saveProfessionGuide } from '../services/professionService';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import { Save, ArrowLeft, Edit2, Check, Upload, Image as ImageIcon, Copy, CheckCircle, Trash2, X, ChevronDown, ChevronUp, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject, getMetadata } from 'firebase/storage';
import { storage } from '../firebase';
import Cropper, { Area } from 'react-easy-crop';

// Profession name mapping
const professionNames: { [key: string]: string } = {
  'general-crafting': 'General Crafting Guide',
  'general-gathering': 'General Gathering Guide',
  'consumable-buyers': 'Consumable Buyer\'s Guide',
  'gear-buyers': 'Gear Buyer\'s Guide',
  alchemy: 'Alchemy',
  blacksmithing: 'Blacksmithing',
  enchanting: 'Enchanting',
  engineering: 'Engineering',
  herbalism: 'Herbalism',
  inscription: 'Inscription',
  jewelcrafting: 'Jewelcrafting',
  leatherworking: 'Leatherworking',
  mining: 'Mining',
  skinning: 'Skinning',
  tailoring: 'Tailoring',
  cooking: 'Cooking',
};

const ProfessionGuide: React.FC = () => {
  const { professionId } = useParams<{ professionId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [content, setContent] = useState<string>('');
  const [editingContent, setEditingContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string>('');
  const [uploadedImageMarkdown, setUploadedImageMarkdown] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<Array<{ url: string; path: string; name: string; timeCreated: number; profession?: string }>>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [deletingImagePath, setDeletingImagePath] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<{ file: File; preview: string } | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'admin';
  const professionName = professionId ? professionNames[professionId] || professionId : 'Unknown Profession';

  // Function to handle copy-to-clipboard links
  const handleCopyLink = async (textToCopy: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedText(textToCopy);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Pre-process content to convert copy: links to a format ReactMarkdown can parse
  // Convert [text](copy:value) to [text](#copy-VALUE) where VALUE is base64 encoded
  const processContent = (content: string): string => {
    // Replace copy: links with anchor links that ReactMarkdown will parse
    return content.replace(/\[([^\]]+)\]\(copy:([^)]+)\)/g, (match, text, copyValue) => {
      // Use #copy- prefix with base64 encoded value so ReactMarkdown parses it as a link
      const encoded = btoa(unescape(encodeURIComponent(copyValue)));
      return `[${text}](#copy-${encoded})`;
    });
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadGuide();
  }, [authLoading, user, professionId]);

  // Load uploaded images when gallery is shown
  useEffect(() => {
    if (showImageGallery && isAdmin) {
      loadUploadedImages();
    }
  }, [showImageGallery, isAdmin, professionId]);

  // Function to load all uploaded images from Storage (recursively)
  const loadUploadedImages = async () => {
    setLoadingImages(true);
    try {
      const images: Array<{ url: string; path: string; name: string; timeCreated: number; profession?: string }> = [];
      
      // Start from profession-guides root
      const rootRef = ref(storage, 'profession-guides');
      
      const loadFolder = async (folderRef: any): Promise<void> => {
        try {
          const result = await listAll(folderRef);
          
          // Process all files in this folder
          for (const itemRef of result.items) {
            try {
              const [url, metadata] = await Promise.all([
                getDownloadURL(itemRef),
                getMetadata(itemRef)
              ]);
              
              // Extract profession from path (profession-guides/{profession}/filename)
              const pathParts = itemRef.fullPath.split('/');
              const profession = pathParts.length > 2 ? pathParts[1] : undefined;
              
              images.push({
                url,
                path: itemRef.fullPath,
                name: itemRef.name,
                timeCreated: metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0,
                profession
              });
            } catch (err) {
              console.error('Error loading image:', itemRef.fullPath, err);
            }
          }
          
          // Recursively process all subfolders
          for (const prefixRef of result.prefixes) {
            await loadFolder(prefixRef);
          }
        } catch (err: any) {
          // If folder doesn't exist or is empty, that's okay
          if (err?.code !== 'storage/object-not-found') {
            console.error('Error listing folder:', folderRef.fullPath, err);
          }
        }
      };
      
      await loadFolder(rootRef);
      
      // Sort by creation time (newest first)
      images.sort((a, b) => b.timeCreated - a.timeCreated);
      setUploadedImages(images);
    } catch (err: any) {
      console.error('Error loading uploaded images:', err);
      setImageUploadError('Failed to load images: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoadingImages(false);
    }
  };

  // Function to delete an uploaded image
  const handleDeleteImage = async (imagePath: string) => {
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImagePath(imagePath);
    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      
      // Remove from local state
      setUploadedImages(prev => prev.filter(img => img.path !== imagePath));
      
      // Clear uploaded markdown if it was for this image
      if (uploadedImageMarkdown && uploadedImageMarkdown.includes(imagePath.split('/').pop() || '')) {
        setUploadedImageMarkdown('');
      }
    } catch (err: any) {
      console.error('Error deleting image:', err);
      setImageUploadError('Failed to delete image: ' + (err?.message || 'Unknown error'));
    } finally {
      setDeletingImagePath(null);
    }
  };

  // Cleanup body overflow when editing ends to prevent scroll lock
  // MDEditor's maximize feature can set overflow: hidden on body/html
  useEffect(() => {
    if (!isEditing) {
      // Use setTimeout to ensure cleanup happens after MDEditor unmounts
      const cleanup = setTimeout(() => {
        // Restore body and html overflow/position styles
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.documentElement.style.overflow = '';
        document.documentElement.style.position = '';
        document.documentElement.style.width = '';
        
        // Remove any fullscreen-related classes that MDEditor might add
        document.body.classList.remove('w-md-editor-fullscreen');
        document.documentElement.classList.remove('w-md-editor-fullscreen');
      }, 100);
      
      return () => clearTimeout(cleanup);
    }
  }, [isEditing]);

  // Additional cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.body.classList.remove('w-md-editor-fullscreen');
      document.documentElement.classList.remove('w-md-editor-fullscreen');
      
      // Cleanup image preview URL
      if (imageToCrop?.preview) {
        URL.revokeObjectURL(imageToCrop.preview);
      }
    };
  }, [imageToCrop]);


  const loadGuide = async () => {
    if (!professionId) return;
    
    setIsLoading(true);
    try {
      const guide = await getProfessionGuide(professionId);
      if (guide) {
        setContent(guide.content || '');
        setEditingContent(guide.content || '');
      } else {
        // No guide exists yet, start with empty content
        setContent('');
        setEditingContent('');
      }
    } catch (error) {
      console.error('Error loading guide:', error);
      setContent('');
      setEditingContent('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!professionId || !user) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const error = await saveProfessionGuide(professionId, editingContent, user.uid);
      if (error) {
        setSaveMessage(`Error: ${error}`);
      } else {
        setContent(editingContent);
        setIsEditing(false);
        setSaveMessage('Guide saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
        
        // Immediately restore scrolling when saving (MDEditor maximize can lock scroll)
        setTimeout(() => {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
          document.documentElement.style.overflow = '';
          document.documentElement.style.position = '';
          document.documentElement.style.width = '';
          document.body.classList.remove('w-md-editor-fullscreen');
          document.documentElement.classList.remove('w-md-editor-fullscreen');
        }, 50);
      }
    } catch (error) {
      console.error('Error saving guide:', error);
      setSaveMessage('Failed to save guide');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingContent(content);
    setIsEditing(false);
    setSaveMessage('');
    
    // Immediately restore scrolling when canceling (MDEditor maximize can lock scroll)
    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.body.classList.remove('w-md-editor-fullscreen');
      document.documentElement.classList.remove('w-md-editor-fullscreen');
    }, 50);
  };

  // Helper function to create a cropped image blob
  const createImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  };

  // Helper function to get cropped image blob
  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas size to match cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  // Handle image file selection - show crop modal
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setImageUploadError('Image size must be less than 10MB');
      return;
    }

    // Create preview URL and show crop modal
    const preview = URL.createObjectURL(file);
    setImageToCrop({ file, preview });
    setShowCropModal(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setImageUploadError('');

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle crop completion and upload
  const handleCropComplete = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    setIsUploadingImage(true);
    setImageUploadError('');

    try {
      // Create cropped image blob
      const croppedBlob = await getCroppedImg(imageToCrop.preview, croppedAreaPixels);
      
      // Create a File from the blob
      const croppedFile = new File([croppedBlob], imageToCrop.file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Create a unique filename
      const timestamp = Date.now();
      const sanitizedFileName = imageToCrop.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storageRef = ref(storage, `profession-guides/${professionId || 'general'}/${timestamp}-${sanitizedFileName}`);
      
      // Upload the cropped image
      await uploadBytes(storageRef, croppedFile);
      
      // Get the download URL
      const imageUrl = await getDownloadURL(storageRef);
      
      // Generate markdown image syntax
      const imageMarkdown = `![${imageToCrop.file.name}](${imageUrl})`;
      
      // Store the markdown to display in a box (user can copy and paste where they want)
      setUploadedImageMarkdown(imageMarkdown);
      
      // Close crop modal and cleanup
      setShowCropModal(false);
      if (imageToCrop.preview) {
        URL.revokeObjectURL(imageToCrop.preview);
      }
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      
      // Refresh image gallery if it's open
      if (showImageGallery) {
        loadUploadedImages();
      }
    } catch (error: any) {
      console.error('Error uploading cropped image:', error);
      setImageUploadError('Failed to upload image: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Handle crop modal cancel
  const handleCropCancel = () => {
    if (imageToCrop?.preview) {
      URL.revokeObjectURL(imageToCrop.preview);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setImageUploadError('');
  };

  // Handle crop area change
  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Handle copying markdown to clipboard
  const handleCopyMarkdown = async () => {
    if (!uploadedImageMarkdown) return;
    
    try {
      await navigator.clipboard.writeText(uploadedImageMarkdown);
      setCopiedText(uploadedImageMarkdown);
      setTimeout(() => {
        setCopiedText(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setImageUploadError('Failed to copy to clipboard');
    }
  };

  // Extract image URL and alt text from markdown
  const extractImageInfo = (markdown: string): { url: string; alt: string } | null => {
    // Match markdown image syntax: ![alt](url)
    const markdownMatch = markdown.match(/!\[([^\]]*)\]\(([^)]+)\)/);
    if (markdownMatch) {
      return { alt: markdownMatch[1], url: markdownMatch[2] };
    }
    // Match HTML img tag: <img src="url" alt="alt" />
    const htmlMatch = markdown.match(/<img[^>]+src=["']([^"']+)["'][^>]*alt=["']([^"']*)["'][^>]*>/);
    if (htmlMatch) {
      return { url: htmlMatch[1], alt: htmlMatch[2] };
    }
    return null;
  };

  // Convert markdown image to HTML with alignment
  const convertToAlignedImage = (alignment: 'left' | 'center' | 'right' | 'inline') => {
    if (!uploadedImageMarkdown) return;
    
    const imageInfo = extractImageInfo(uploadedImageMarkdown);
    if (!imageInfo) return;

    let htmlImage = '';
    
    if (alignment === 'center') {
      // Center alignment using div wrapper
      htmlImage = `<div style="text-align: center;">\n  <img src="${imageInfo.url}" alt="${imageInfo.alt}" style="max-width: 100%; height: auto;" />\n</div>`;
    } else if (alignment === 'left') {
      // Left alignment using float
      htmlImage = `<img src="${imageInfo.url}" alt="${imageInfo.alt}" style="float: left; margin-right: 1em; margin-bottom: 1em; max-width: 100%; height: auto;" />`;
    } else if (alignment === 'right') {
      // Right alignment using float
      htmlImage = `<img src="${imageInfo.url}" alt="${imageInfo.alt}" style="float: right; margin-left: 1em; margin-bottom: 1em; max-width: 100%; height: auto;" />`;
    } else {
      // Inline (default markdown behavior)
      htmlImage = `![${imageInfo.alt}](${imageInfo.url})`;
    }

    setUploadedImageMarkdown(htmlImage);
  };

  // Convert all markdown images in editor content to aligned HTML
  const convertAllImagesInContent = (alignment: 'left' | 'center' | 'right') => {
    if (!editingContent) return;

    let updatedContent = editingContent;

    // Find all markdown images: ![alt](url)
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    
    updatedContent = updatedContent.replace(imageRegex, (match, alt, url) => {
      let htmlImage = '';
      
      if (alignment === 'center') {
        htmlImage = `<div style="text-align: center;">\n  <img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;" />\n</div>`;
      } else if (alignment === 'left') {
        htmlImage = `<img src="${url}" alt="${alt}" style="float: left; margin-right: 1em; margin-bottom: 1em; max-width: 100%; height: auto;" />`;
      } else if (alignment === 'right') {
        htmlImage = `<img src="${url}" alt="${alt}" style="float: right; margin-left: 1em; margin-bottom: 1em; max-width: 100%; height: auto;" />`;
      }
      
      return htmlImage;
    });

    setEditingContent(updatedContent);
  };

  // Convert selected text (if it's an image) or find image at cursor
  const alignImageInEditor = (alignment: 'left' | 'center' | 'right') => {
    if (!editingContent) return;

    // Try to get selection from the editor (MDEditor doesn't expose this easily)
    // So we'll search for images near the cursor position or in selected content
    // For now, let's add a helper that finds the first image and converts it
    // Or we can convert all images if no specific selection

    // Simple approach: Convert all markdown images to aligned HTML
    // User can undo/redo if they don't like it
    convertAllImagesInContent(alignment);
  };

  if (authLoading || !user) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold mb-4 gradient-text">{authLoading ? 'Loading...' : 'Redirecting to login...'}</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold mb-4 gradient-text">Loading guide...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/professions')}
              className="p-2 rounded-lg glass hover:bg-surface-light transition-colors"
              aria-label="Back to professions"
            >
              <ArrowLeft size={24} className="text-cyan-400" />
            </button>
            <h1 className="text-3xl md:text-4xl font-bold">
              <span className="gradient-text">{professionName}</span>
            </h1>
          </div>
          
          {isAdmin && (
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setShowImageGallery(!showImageGallery);
                      if (!showImageGallery) {
                        loadUploadedImages();
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 glass text-text-primary rounded-lg hover:bg-surface-light transition-colors"
                  >
                    <ImageIcon size={18} />
                    Manage Images
                    {showImageGallery ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 btn-neon rounded-lg"
                  >
                    <Edit2 size={18} />
                    Edit Guide
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                    id="image-upload-input"
                  />
                  <label
                    htmlFor="image-upload-input"
                    className={`flex items-center gap-2 px-4 py-2 glass text-text-primary rounded-lg hover:bg-surface-light transition-colors cursor-pointer ${isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isUploadingImage ? (
                      <>
                        <Upload size={18} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ImageIcon size={18} />
                        Upload Image
                      </>
                    )}
                  </label>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 glass text-text-primary rounded-lg hover:bg-surface-light transition-colors"
                    disabled={isSaving || isUploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-4 py-2 btn-neon rounded-lg"
                    disabled={isSaving || isUploadingImage}
                  >
                    <Save size={18} />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            saveMessage.includes('Error') || saveMessage.includes('Failed')
              ? 'bg-red-900/30 border border-red-500 text-red-300'
              : 'bg-green-900/30 border border-green-500 text-green-300'
          }`}>
            {saveMessage}
          </div>
        )}

        {/* Image Gallery/Management Section */}
        {isAdmin && showImageGallery && (
          <div className="mb-6 glass p-6 rounded-xl border border-cyan-400/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-text-primary">
                Uploaded Images {professionId && `(${professionName})`}
              </h2>
              <button
                onClick={() => setShowImageGallery(false)}
                className="p-1 rounded hover:bg-surface-light transition-colors"
                aria-label="Close gallery"
              >
                <X size={20} className="text-text-secondary" />
              </button>
            </div>

            {loadingImages ? (
              <div className="text-center py-8 text-text-secondary">
                Loading images...
              </div>
            ) : uploadedImages.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                No images uploaded yet.
              </div>
            ) : (
              <>
                <div className="mb-4 text-sm text-text-secondary">
                  Total: {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''}
                  {professionId && (
                    <span className="ml-2">
                      | {uploadedImages.filter(img => img.profession === professionId).length} from this guide
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {uploadedImages.map((image) => (
                    <div
                      key={image.path}
                      className="relative group glass rounded-lg overflow-hidden border border-border/50 hover:border-cyan-400/50 transition-colors"
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-32 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => handleDeleteImage(image.path)}
                          disabled={deletingImagePath === image.path}
                          className="p-2 glass rounded-lg hover:bg-red-500/30 transition-colors text-red-400 hover:text-red-300 disabled:opacity-50"
                          title="Delete image"
                        >
                          {deletingImagePath === image.path ? (
                            <Trash2 size={20} className="animate-spin" />
                          ) : (
                            <Trash2 size={20} />
                          )}
                        </button>
                      </div>
                      <div className="p-2 bg-surface/80">
                        <p className="text-xs text-text-secondary truncate" title={image.name}>
                          {image.name}
                        </p>
                        {image.profession && (
                          <p className="text-xs text-cyan-400">
                            {professionNames[image.profession] || image.profession}
                          </p>
                        )}
                        <p className="text-xs text-text-muted">
                          {image.timeCreated ? new Date(image.timeCreated).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Image Upload Error */}
        {imageUploadError && (
          <div className="mb-4 p-4 rounded-lg bg-red-900/30 border border-red-500 text-red-300">
            {imageUploadError}
            <button
              onClick={() => setImageUploadError('')}
              className="ml-2 text-red-400 hover:text-red-200 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Uploaded Image Markdown Box */}
        {isEditing && uploadedImageMarkdown && (
          <div className="mb-4 p-4 rounded-lg glass border border-cyan-400/30">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-secondary">
                Image Markdown (Copy and paste into editor):
              </label>
              <div className="flex items-center gap-2">
                {/* Alignment Buttons */}
                <div className="flex items-center gap-1 glass rounded-lg p-1">
                  <button
                    onClick={() => convertToAlignedImage('left')}
                    className="p-1.5 rounded hover:bg-surface-light transition-colors text-text-secondary hover:text-cyan-400"
                    title="Align Left"
                  >
                    <AlignLeft size={16} />
                  </button>
                  <button
                    onClick={() => convertToAlignedImage('center')}
                    className="p-1.5 rounded hover:bg-surface-light transition-colors text-text-secondary hover:text-cyan-400"
                    title="Align Center"
                  >
                    <AlignCenter size={16} />
                  </button>
                  <button
                    onClick={() => convertToAlignedImage('right')}
                    className="p-1.5 rounded hover:bg-surface-light transition-colors text-text-secondary hover:text-cyan-400"
                    title="Align Right"
                  >
                    <AlignRight size={16} />
                  </button>
                </div>
                <button
                  onClick={handleCopyMarkdown}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm glass hover:bg-surface-light transition-colors rounded-lg text-cyan-400 hover:text-cyan-300"
                >
                  {copiedText === uploadedImageMarkdown ? (
                    <>
                      <CheckCircle size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="p-3 bg-surface/50 rounded border border-border/50 font-mono text-sm text-text-primary break-all whitespace-pre-wrap">
              {uploadedImageMarkdown}
            </div>
            <div className="flex items-center justify-between mt-2">
              <button
                onClick={() => setUploadedImageMarkdown('')}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors underline"
              >
                Clear
              </button>
              <span className="text-xs text-text-muted">
                Tip: Click alignment buttons to change how the image appears, then copy the updated markdown
              </span>
            </div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && imageToCrop && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <div className="glass rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-text-primary">
                  Crop Image
                </h2>
                <button
                  onClick={handleCropCancel}
                  className="p-2 rounded-lg hover:bg-surface-light transition-colors"
                  disabled={isUploadingImage}
                >
                  <X size={24} className="text-text-secondary" />
                </button>
              </div>
              
              <div className="relative w-full h-96 bg-surface rounded-lg mb-4 overflow-hidden">
                <Cropper
                  image={imageToCrop.preview}
                  crop={crop}
                  zoom={zoom}
                  aspect={undefined}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    },
                  }}
                />
              </div>
              
              <div className="flex flex-col gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Zoom: {zoom.toFixed(2)}x
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full accent-cyan-400"
                  />
                </div>
                <p className="text-sm text-text-secondary">
                  Drag to reposition • Scroll to zoom • Click and drag corners to adjust crop area
                </p>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCropCancel}
                  className="px-4 py-2 glass text-text-primary rounded-lg hover:bg-surface-light transition-colors"
                  disabled={isUploadingImage}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropComplete}
                  className="flex items-center gap-2 px-4 py-2 btn-neon rounded-lg"
                  disabled={isUploadingImage || !croppedAreaPixels}
                >
                  {isUploadingImage ? (
                    <>
                      <Upload size={18} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Crop & Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="glass p-6 md:p-8 rounded-xl">
          {isAdmin && isEditing ? (
            <>
              {/* Image Alignment Helper Toolbar */}
              <div className="mb-4 p-3 glass rounded-lg border border-cyan-400/30">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-text-secondary">Align Images:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => alignImageInEditor('left')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm glass hover:bg-surface-light transition-colors rounded-lg text-cyan-400 hover:text-cyan-300"
                      title="Convert all markdown images to left-aligned HTML"
                    >
                      <AlignLeft size={16} />
                      Left All
                    </button>
                    <button
                      onClick={() => alignImageInEditor('center')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm glass hover:bg-surface-light transition-colors rounded-lg text-cyan-400 hover:text-cyan-300"
                      title="Convert all markdown images to center-aligned HTML"
                    >
                      <AlignCenter size={16} />
                      Center All
                    </button>
                    <button
                      onClick={() => alignImageInEditor('right')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm glass hover:bg-surface-light transition-colors rounded-lg text-cyan-400 hover:text-cyan-300"
                      title="Convert all markdown images to right-aligned HTML"
                    >
                      <AlignRight size={16} />
                      Right All
                    </button>
                  </div>
                  <span className="text-xs text-text-muted ml-auto">
                    Converts all ![alt](url) markdown images to aligned HTML
                  </span>
                </div>
              </div>
              <div className="markdown-editor-container">
                <MDEditor
                  value={editingContent}
                  onChange={(value) => setEditingContent(value || '')}
                  preview="live"
                  hideToolbar={false}
                  data-color-mode="light"
                />
              </div>
            </>
          ) : (
            <div className="markdown-content prose prose-invert prose-cyan max-w-none">
              {content ? (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSlug, rehypeRaw]}
                  components={{
                    a: ({ node, href, children, ...props }: any) => {
                      // Check if this is a copy-to-clipboard link
                      // Format: #copy-BASE64_ENCODED_VALUE
                      if (href && href.startsWith('#copy-')) {
                        try {
                          // Decode the base64 encoded value
                          const encoded = href.substring(6); // Remove '#copy-'
                          const textToCopy = decodeURIComponent(escape(atob(encoded)));
                          const isCopied = copiedText === textToCopy;
                          return (
                            <button
                              onClick={(e) => handleCopyLink(textToCopy, e)}
                              className="copy-link-button"
                              title="Click to copy to clipboard"
                              type="button"
                            >
                              {isCopied ? (
                                <span className="copy-link-copied">
                                  <Check size={14} className="inline mr-1" />
                                  Copied!
                                </span>
                              ) : (
                                <span className="copy-link-text">{children}</span>
                              )}
                            </button>
                          );
                        } catch (err) {
                          console.error('Failed to decode copy link:', err);
                          // Fallback to regular link if decoding fails
                          return <a href={href} {...props}>{children}</a>;
                        }
                      }
                      // Also handle direct copy: links (in case preprocessing didn't run)
                      if (href && (href.startsWith('copy:') || href.startsWith('copy%3A'))) {
                        const textToCopy = href.startsWith('copy:') 
                          ? href.substring(5) 
                          : decodeURIComponent(href.substring(7));
                        const isCopied = copiedText === textToCopy;
                        return (
                          <button
                            onClick={(e) => handleCopyLink(textToCopy, e)}
                            className="copy-link-button"
                            title="Click to copy to clipboard"
                            type="button"
                          >
                            {isCopied ? (
                              <span className="copy-link-copied">
                                <Check size={14} className="inline mr-1" />
                                Copied!
                              </span>
                            ) : (
                              <span className="copy-link-text">{children}</span>
                            )}
                          </button>
                        );
                      }
                      // Handle anchor links (same-page links starting with #)
                      if (href && href.startsWith('#')) {
                        return (
                          <a 
                            href={href} 
                            {...props}
                            onClick={(e) => {
                              e.preventDefault();
                              const id = href.substring(1);
                              const element = document.getElementById(id);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                // Update URL without reload
                                window.history.pushState(null, '', `#${id}`);
                              }
                            }}
                          >
                            {children}
                          </a>
                        );
                      }
                      // Regular link
                      return <a href={href} {...props}>{children}</a>;
                    },
                    h1: ({ node, children, ...props }: any) => {
                      // Ensure headings have IDs for anchor links
                      const id = node?.data?.id as string || 
                        typeof children === 'string' 
                          ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                          : '';
                      return <h1 id={id} {...props}>{children}</h1>;
                    },
                    h2: ({ node, children, ...props }: any) => {
                      const id = node?.data?.id as string || 
                        typeof children === 'string' 
                          ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                          : '';
                      return <h2 id={id} {...props}>{children}</h2>;
                    },
                    h3: ({ node, children, ...props }: any) => {
                      const id = node?.data?.id as string || 
                        typeof children === 'string' 
                          ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                          : '';
                      return <h3 id={id} {...props}>{children}</h3>;
                    },
                    h4: ({ node, children, ...props }: any) => {
                      const id = node?.data?.id as string || 
                        typeof children === 'string' 
                          ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                          : '';
                      return <h4 id={id} {...props}>{children}</h4>;
                    },
                    td: ({ children, ...props }: any) => {
                      // Process table cells to handle markdown links
                      const cellContent = React.Children.toArray(children);
                      return <td {...props}>{cellContent}</td>;
                    },
                  }}
                >
                  {processContent(content)}
                </ReactMarkdown>
              ) : (
                <div className="text-center py-12 text-text-secondary">
                  <p className="text-lg mb-2">No guide content available yet.</p>
                  {isAdmin ? (
                    <p className="text-sm">Click "Edit Guide" to create content for this profession.</p>
                  ) : (
                    <p className="text-sm">Check back later for guide content.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom styles for markdown editor */}
      <style>{`
        .markdown-editor-container {
          min-height: 500px;
        }
        
        /* Editor container - white background */
        .markdown-editor-container .w-md-editor,
        .markdown-editor-container .w-md-editor-text,
        .markdown-editor-container .w-md-editor-text-textarea,
        .markdown-editor-container .w-md-editor-text-pre,
        .markdown-editor-container .w-md-editor-text-input,
        .markdown-editor-container .w-md-editor-preview,
        .markdown-editor-container .w-md-editor-preview-wrapper {
          background-color: #ffffff !important;
        }
        
        /* All text content - black color */
        .markdown-editor-container .w-md-editor-text *,
        .markdown-editor-container .w-md-editor-text-textarea *,
        .markdown-editor-container .w-md-editor-text-textarea textarea,
        .markdown-editor-container .w-md-editor-text-textarea pre,
        .markdown-editor-container .w-md-editor-text-textarea code,
        .markdown-editor-container .w-md-editor-text-input *,
        .markdown-editor-container .w-md-editor-preview,
        .markdown-editor-container textarea {
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
          opacity: 1 !important;
          filter: none !important;
          mix-blend-mode: normal !important;
        }
        
        /* Placeholder text */
        .markdown-editor-container textarea::placeholder,
        .markdown-editor-container textarea::-webkit-input-placeholder,
        .markdown-editor-container textarea::-moz-placeholder,
        .markdown-editor-container textarea:-ms-input-placeholder {
          color: rgba(0, 0, 0, 0.5) !important;
          -webkit-text-fill-color: rgba(0, 0, 0, 0.5) !important;
        }
        
        /* Text selection */
        .markdown-editor-container textarea::selection,
        .markdown-editor-container textarea::-moz-selection {
          background-color: #b3d4fc !important;
          color: #000000 !important;
        }
        
        /* Remove pseudo-element overlays */
        .markdown-editor-container .w-md-editor-text-textarea::before,
        .markdown-editor-container .w-md-editor-text-textarea::after,
        .markdown-editor-container .w-md-editor-text-textarea *::before,
        .markdown-editor-container .w-md-editor-text-textarea *::after {
          display: none !important;
          content: none !important;
        }
        
        /* Toolbar styling */
        .markdown-editor-container .w-md-editor-toolbar {
          background-color: #f3f4f6 !important;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3) !important;
        }
        .markdown-editor-container .w-md-editor-toolbar button {
          color: #000000 !important;
        }
        .markdown-editor-container .w-md-editor-toolbar button:hover {
          background-color: rgba(0, 0, 0, 0.1) !important;
        }
        .markdown-editor-container .w-md-editor-toolbar {
          background-color: #f3f4f6 !important;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3) !important;
        }
        .markdown-editor-container .w-md-editor-toolbar button {
          color: #000000 !important;
        }
        .markdown-editor-container .w-md-editor-toolbar button:hover {
          background-color: rgba(0, 0, 0, 0.1) !important;
        }
        .markdown-content {
          color: #e5e7eb;
        }
        .markdown-content h1,
        .markdown-content h2,
        .markdown-content h3,
        .markdown-content h4 {
          color: #67e8f9;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        .markdown-content h1 {
          font-size: 2em;
          border-bottom: none;
          padding-bottom: 0;
          scroll-margin-top: 120px; /* Account for fixed navbar */
        }
        .markdown-content h1[id]:target,
        .markdown-content h2[id]:target,
        .markdown-content h3[id]:target,
        .markdown-content h4[id]:target {
          scroll-margin-top: 120px;
        }
        .markdown-content h2 {
          font-size: 1.5em;
          border-bottom: none;
          padding-bottom: 0;
          scroll-margin-top: 120px;
        }
        .markdown-content h3 {
          scroll-margin-top: 120px;
        }
        .markdown-content h4 {
          scroll-margin-top: 120px;
        }
        .markdown-content code {
          background-color: rgba(0, 0, 0, 0.3);
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
          color: #fbbf24;
        }
        .markdown-content pre {
          background-color: rgba(0, 0, 0, 0.5);
          padding: 1em;
          border-radius: 6px;
          overflow-x: auto;
        }
        .markdown-content pre code {
          background-color: transparent;
          padding: 0;
          color: #e5e7eb;
        }
        .markdown-content a {
          color: #67e8f9;
          text-decoration: underline;
        }
        .markdown-content a:hover {
          color: #22d3ee;
        }
        .markdown-content p,
        .markdown-content .prose p {
          margin-top: 0 !important;
          margin-bottom: 1em !important;
        }
        .markdown-content p:last-child {
          margin-bottom: 0 !important;
        }
        .markdown-content img,
        .markdown-content .prose img {
          max-width: 100% !important;
          height: auto !important;
          display: block;
        }
        .markdown-content img[style*="float: left"],
        .markdown-content img[style*="float:left"] {
          float: left !important;
          margin-right: 1em !important;
          margin-bottom: 1em !important;
        }
        .markdown-content img[style*="float: right"],
        .markdown-content img[style*="float:right"] {
          float: right !important;
          margin-left: 1em !important;
          margin-bottom: 1em !important;
        }
        .markdown-content div[style*="text-align: center"],
        .markdown-content div[style*="text-align:center"] {
          text-align: center !important;
          margin: 1em 0 !important;
        }
        .markdown-content div[style*="text-align: center"] img,
        .markdown-content div[style*="text-align:center"] img {
          margin: 0 auto !important;
        }
        .markdown-content ul,
        .markdown-content .prose ul {
          margin-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
          list-style-type: disc !important;
          list-style-position: outside !important;
          padding-left: 1em;
        }
        .markdown-content ol,
        .markdown-content .prose ol {
          margin-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
          list-style-type: decimal !important;
          list-style-position: outside !important;
          padding-left: 1em;
        }
        .markdown-content li,
        .markdown-content .prose li {
          margin-top: 0.25em;
          margin-bottom: 0.25em;
          display: list-item !important;
          list-style: inherit !important;
        }
        .markdown-content ul li,
        .markdown-content .prose ul li {
          list-style-type: disc !important;
        }
        .markdown-content ol li,
        .markdown-content .prose ol li {
          list-style-type: decimal !important;
        }
        .markdown-content blockquote {
          border-left: 4px solid #67e8f9;
          padding-left: 1em;
          margin-left: 0;
          color: #9ca3af;
          font-style: italic;
        }
        .markdown-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
          display: table;
          overflow-x: auto;
        }
        .markdown-content table thead {
          display: table-header-group;
        }
        .markdown-content table tbody {
          display: table-row-group;
        }
        .markdown-content table tr {
          display: table-row;
          border-top: 1px solid rgba(75, 85, 99, 0.5);
        }
        .markdown-content table tr:nth-child(2n) {
          background-color: rgba(75, 85, 99, 0.1);
        }
        .markdown-content th,
        .markdown-content td {
          display: table-cell;
          border: 1px solid rgba(75, 85, 99, 0.5);
          padding: 0.5em 1em;
          text-align: left;
        }
        .markdown-content th {
          background-color: rgba(75, 85, 99, 0.3);
          font-weight: bold;
        }
        .markdown-content tbody tr:hover {
          background-color: rgba(75, 85, 99, 0.2);
        }
        .copy-link-button {
          background: none;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          color: #67e8f9;
          font-family: inherit;
          font-size: inherit;
          text-decoration: underline;
          transition: all 0.2s ease;
        }
        .copy-link-button:hover {
          color: #22d3ee;
        }
        .copy-link-text {
          display: inline;
        }
        .copy-link-copied {
          display: inline-flex;
          align-items: center;
          color: #34d399;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default ProfessionGuide;

